import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, MoreHorizontal, ArrowLeftRight, ChevronDown, ChevronUp } from "lucide-react";
import { ORDER_SIDE, ORDER_TYPE, ROLE, TIF } from "../../lib/paper/engine";

const fmt = (p, dp = 2) => (p == null || Number.isNaN(p) ? "—" : p.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp }));
const fmtUnits = (u) => (u == null || Number.isNaN(u) ? "0" : u.toLocaleString(undefined, { maximumFractionDigits: 6 }));

// Floating, draggable order ticket. Everything is simulated — this just builds
// an order spec and hands it to the engine via onSubmit.
export default function OrderTicket({ symbol, quote, leverage = 10, availableFunds = 0, settings, initialSide = ORDER_SIDE.BUY, onSubmit, onSetLeverage, onClose }) {
  const [side, setSide] = useState(initialSide);
  const [type, setType] = useState(ORDER_TYPE.MARKET);
  const [tif, setTif] = useState(TIF.GTC);
  const [priceStr, setPriceStr] = useState("");
  const [marginStr, setMarginStr] = useState("");
  const [sizeMode, setSizeMode] = useState("usd"); // 'usd' margin | 'units'
  const [pct, setPct] = useState(0);
  const [showExits, setShowExits] = useState(false);
  const [tpOn, setTpOn] = useState(false);
  const [slOn, setSlOn] = useState(false);
  const [tpStr, setTpStr] = useState("");
  const [slStr, setSlStr] = useState("");
  const [error, setError] = useState(null);

  const bid = quote?.bid;
  const ask = quote?.ask;
  const last = quote?.last;
  const spread = bid != null && ask != null ? ask - bid : null;
  const refPrice = type === ORDER_TYPE.MARKET ? (side === ORDER_SIDE.BUY ? ask : bid) : parseFloat(priceStr) || last;

  // ── margin ⇄ units ──────────────────────────────────────────────────────
  const margin = parseFloat(marginStr) || 0;
  const units = refPrice ? (margin * leverage) / refPrice : 0;
  const notional = margin * leverage;

  // slider drives margin as % of available funds
  const setPctAndMargin = (p) => {
    setPct(p);
    setMarginStr(((availableFunds * p) / 100).toFixed(2));
  };
  useEffect(() => {
    // keep slider roughly in sync if user types margin
    if (availableFunds > 0) setPct(Math.min(100, Math.round((margin / availableFunds) * 100)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marginStr, availableFunds]);

  // ── drag ──────────────────────────────────────────────────────────────────
  const [pos, setPos] = useState({ x: null, y: null });
  const drag = useRef(null);
  const onDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x ?? 0, oy: pos.y ?? 0 };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    setPos({ x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) });
  };
  const onUp = () => {
    drag.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
  };
  useEffect(() => () => onUp(), []);

  // ── risk / reward ──────────────────────────────────────────────────────────
  const tp = parseFloat(tpStr);
  const sl = parseFloat(slStr);
  const rr = useMemo(() => {
    if (!(tpOn && slOn && refPrice && !Number.isNaN(tp) && !Number.isNaN(sl))) return null;
    const reward = Math.abs(tp - refPrice);
    const risk = Math.abs(refPrice - sl);
    return risk > 0 ? reward / risk : null;
  }, [tpOn, slOn, tp, sl, refPrice]);

  const distTicks = (target) => {
    if (!target || !refPrice) return null;
    return (target - refPrice).toFixed(2);
  };

  // ── submit ──────────────────────────────────────────────────────────────
  const submit = () => {
    setError(null);
    if (!(units > 0)) return setError("Enter a size");
    if (type !== ORDER_TYPE.MARKET && !(parseFloat(priceStr) > 0)) return setError("Enter a price");
    const isLong = side === ORDER_SIDE.BUY;
    if (tpOn) {
      if (isLong && tp <= refPrice) return setError("Take profit must be above entry");
      if (!isLong && tp >= refPrice) return setError("Take profit must be below entry");
    }
    if (slOn) {
      if (isLong && sl >= refPrice) return setError("Stop loss must be below entry");
      if (!isLong && sl <= refPrice) return setError("Stop loss must be above entry");
    }
    const brackets = [];
    if (tpOn && tp > 0) brackets.push({ role: ROLE.TP, price: tp, qty: units });
    if (slOn && sl > 0) brackets.push({ role: ROLE.SL, price: sl, qty: units });

    const spec = {
      symbol,
      side,
      type,
      qty: units,
      limitPrice: type === ORDER_TYPE.LIMIT ? parseFloat(priceStr) : null,
      stopPrice: type === ORDER_TYPE.STOP ? parseFloat(priceStr) : null,
      tif,
      brackets: brackets.length ? brackets : null,
    };
    const res = onSubmit(spec);
    if (res?.error) setError(res.error);
    else onClose?.();
  };

  const isBuy = side === ORDER_SIDE.BUY;
  const sideColor = isBuy ? "bg-blue-600 hover:bg-blue-500" : "bg-rose-600 hover:bg-rose-500";
  const submitLabel = `${isBuy ? "Buy" : "Sell"} ${fmtUnits(units)} ${symbol}USDT.P ${type === ORDER_TYPE.MARKET ? "MARKET" : `@ ${fmt(parseFloat(priceStr))} ${type.toUpperCase()}`}`;

  return (
    <div
      className="fixed z-50 w-[320px] bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl text-xs text-zinc-200"
      style={{ left: pos.x != null ? pos.x : "50%", top: pos.y != null ? pos.y : 90, transform: pos.x != null ? "none" : "translateX(-50%)" }}
    >
      {/* header */}
      <div className="flex items-center justify-between px-3 h-9 border-b border-zinc-800 cursor-move select-none" onMouseDown={onDown}>
        <span className="font-semibold text-zinc-100">{symbol}USDT.P</span>
        <div className="flex items-center gap-1">
          <MoreHorizontal className="w-3.5 h-3.5 text-zinc-500" />
          <button onClick={onClose} className="p-0.5 hover:bg-zinc-800 rounded"><X className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Sell | Buy selector */}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setSide(ORDER_SIDE.SELL)}
            className={`rounded p-2 border ${!isBuy ? "border-rose-500 bg-rose-500/10" : "border-zinc-700"}`}>
            <div className="text-[9px] uppercase text-rose-400">Sell</div>
            <div className="font-mono tabular-nums text-rose-300">{fmt(bid)}</div>
          </button>
          <button onClick={() => setSide(ORDER_SIDE.BUY)}
            className={`rounded p-2 border ${isBuy ? "border-blue-500 bg-blue-500/10" : "border-zinc-700"}`}>
            <div className="text-[9px] uppercase text-blue-400">Buy</div>
            <div className="font-mono tabular-nums text-blue-300">{fmt(ask)}</div>
          </button>
        </div>
        <div className="text-center text-[10px] text-zinc-500">spread {spread != null ? fmt(spread) : "—"}</div>

        {/* type tabs */}
        <div className="flex border-b border-zinc-800">
          {[ORDER_TYPE.MARKET, ORDER_TYPE.LIMIT, ORDER_TYPE.STOP].map((t) => (
            <button key={t} onClick={() => setType(t)}
              className={`flex-1 py-1.5 capitalize ${type === t ? "text-white border-b-2 border-emerald-500" : "text-zinc-500"}`}>
              {t}
            </button>
          ))}
        </div>

        {/* price (limit/stop) */}
        {type !== ORDER_TYPE.MARKET && (
          <div>
            <label className="text-[10px] text-zinc-500">Price</label>
            <div className="flex items-center bg-zinc-800 rounded px-2">
              <input value={priceStr} onChange={(e) => setPriceStr(e.target.value)} placeholder={fmt(last)}
                className="flex-1 bg-transparent py-1.5 outline-none font-mono" />
              <span className="text-[10px] text-zinc-500">{distTicks(parseFloat(priceStr)) != null ? `${distTicks(parseFloat(priceStr))}` : ""}</span>
            </div>
          </div>
        )}

        {/* leverage */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-zinc-500">Leverage</span>
          <div className="flex items-center gap-1">
            {[1, 5, 10, 25, 50, 100].map((L) => (
              <button key={L} onClick={() => onSetLeverage?.(L)}
                className={`px-1.5 py-0.5 rounded text-[10px] ${leverage === L ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-400"}`}>
                {L}x
              </button>
            ))}
          </div>
        </div>

        {/* size */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[10px] text-zinc-500">Size ({sizeMode === "usd" ? "USD margin" : "units"})</label>
            <button onClick={() => setSizeMode((m) => (m === "usd" ? "units" : "usd"))} className="text-zinc-500 hover:text-zinc-300">
              <ArrowLeftRight className="w-3 h-3" />
            </button>
          </div>
          <div className="flex items-center bg-zinc-800 rounded px-2">
            <input value={marginStr} onChange={(e) => setMarginStr(e.target.value)} placeholder="0.00"
              className="flex-1 bg-transparent py-1.5 outline-none font-mono" />
            <span className="text-[10px] text-zinc-500">{fmtUnits(units)} units</span>
          </div>
        </div>

        {/* slider */}
        <div>
          <input type="range" min="0" max="100" value={pct} onChange={(e) => setPctAndMargin(Number(e.target.value))}
            className="w-full h-1.5 accent-emerald-500 cursor-pointer" />
          <div className="flex justify-between text-[9px] text-zinc-500"><span>0%</span><span>{pct}% of avail</span><span>100%</span></div>
        </div>

        {/* info box */}
        <div className="bg-zinc-800/40 rounded p-2 space-y-1 text-[10px]">
          <div className="flex justify-between"><span className="text-zinc-500">Trade value</span><span className="font-mono">{fmt(notional)} USD ({leverage}x)</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Available margin</span><span className="font-mono">{fmt(availableFunds)} USD</span></div>
        </div>

        {/* exits */}
        <div className="border-t border-zinc-800 pt-2">
          <button onClick={() => setShowExits((s) => !s)} className="flex items-center gap-1 text-[10px] text-zinc-400 w-full">
            {showExits ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Exits {rr ? `· R/R ${rr.toFixed(2)}` : ""}
          </button>
          {showExits && (
            <div className="space-y-2 mt-2">
              <div>
                <label className="flex items-center gap-1 text-[10px] text-emerald-400">
                  <input type="checkbox" checked={tpOn} onChange={(e) => setTpOn(e.target.checked)} /> Take profit
                </label>
                {tpOn && (
                  <input value={tpStr} onChange={(e) => setTpStr(e.target.value)} placeholder="TP price"
                    className="w-full bg-zinc-800 rounded px-2 py-1.5 outline-none font-mono mt-1" />
                )}
              </div>
              <div>
                <label className="flex items-center gap-1 text-[10px] text-rose-400">
                  <input type="checkbox" checked={slOn} onChange={(e) => setSlOn(e.target.checked)} /> Stop loss
                </label>
                {slOn && (
                  <input value={slStr} onChange={(e) => setSlStr(e.target.value)} placeholder="SL price"
                    className="w-full bg-zinc-800 rounded px-2 py-1.5 outline-none font-mono mt-1" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* TIF */}
        {type !== ORDER_TYPE.MARKET && (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-zinc-500">Time in force</span>
            <select value={tif} onChange={(e) => setTif(e.target.value)} className="bg-zinc-800 rounded px-2 py-1 text-[10px] outline-none">
              {[TIF.GTC, TIF.DAY, TIF.WEEK].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}

        {error && <div className="text-[10px] text-rose-400 bg-rose-500/10 rounded px-2 py-1">{error}</div>}

        <button onClick={submit} className={`w-full py-2 rounded text-white font-semibold ${sideColor}`}>{submitLabel}</button>
      </div>
    </div>
  );
}
